import React from 'react';
import {
  FeedbackMatchAnyContext,
  FeedbackRecipientContext,
  FeedbackTriggerType,
  FeedbackReviewerIsRecipient,
} from 'records/FeedbackTrigger';

/**
 * HOC that manages the recipient part of the trigger.  The component must add the <UserContextViewPlusButton...> and then this handles the rest
 * The gist of the json that this manages looks like
 * {
 *   type: "matchAll",
 *   matchAll: [
 *     {
 *       type: "matchAny",
 *       matchAny: [
 *         {
 *           user condition
 *         },
 *         {
 *           another user condition
 *         }
 *         ...
 *       ]
 *     }
 *   ]
 * }
 */
export default function withRecipients(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.getRecipients = this.getRecipients.bind(this);
      this.getRecipientsIndex = this.getRecipientsIndex.bind(this);
      this.addRecipient = this.addRecipient.bind(this);
      this.removeRecipient = this.removeRecipient.bind(this);
      this.updateRecipient = this.updateRecipient.bind(this);
      this.toRecipient = this.toRecipient.bind(this);
    }

    // convert to feedback recipient, embed in FeedbackRecipientContext if needed
    toRecipient(recipient) {
      if (recipient instanceof FeedbackReviewerIsRecipient) {
        return recipient;
      } else {
        return new FeedbackRecipientContext({
          recipient: recipient,
        });
      }
    }

    addRecipient(newRecipient) {
      const recipientIndex = this.getRecipientsIndex();
      const recipientContext = this.toRecipient(newRecipient);
      const newContext =
        recipientIndex >= 0
          ? this.props.context.updateIn(['matchAll', recipientIndex, 'matchAny'], matchAny =>
              matchAny.push(recipientContext)
            )
          : this.props.context.update('matchAll', matchAll =>
              matchAll.push(new FeedbackMatchAnyContext({matchAny: [recipientContext]}))
            );
      this.props.contextChange(newContext);
    }

    removeRecipient(index) {
      const recipients = this.getRecipients();

      const newContext =
        recipients.size > 1
          ? this.props.context.removeIn(['matchAll', this.getRecipientsIndex(), 'matchAny', index])
          : this.props.context.removeIn(['matchAll', this.getRecipientsIndex()]);
      this.props.contextChange(newContext);
    }

    updateRecipient(index, newRecipient) {
      const recipientContext = this.toRecipient(newRecipient);
      const newContext = this.props.context.setIn(
        ['matchAll', this.getRecipientsIndex(), 'matchAny', index],
        recipientContext
      );
      this.props.contextChange(newContext);
    }

    getRecipients() {
      const matchAny = this.props.context.matchAll.find(
        item => item.type === FeedbackTriggerType.MATCH_ANY
      );
      return (
        matchAny &&
        matchAny.matchAny.map(item =>
          item.type === FeedbackTriggerType.RECIPIENT ? item.recipient : item
        )
      );
    }

    /**
     * Get the index of the condition that matchAny recipients
     */
    getRecipientsIndex() {
      const matchAny = this.props.context.matchAll.find(
        item => item.type === FeedbackTriggerType.MATCH_ANY
      );
      return this.props.context.matchAll.indexOf(matchAny);
    }

    render() {
      const recipients = this.getRecipients();
      return (
        <WrappedComponent
          {...this.props}
          addRecipient={this.addRecipient}
          removeRecipient={this.removeRecipient}
          updateRecipient={this.updateRecipient}
          recipients={recipients}
        />
      );
    }
  };
}
